import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';

const VendorAccountSettings = ({
  user,
  newEmail,
  setNewEmail,
  newPhone,
  setNewPhone,
  passwordData,
  setPasswordData,
  securityQuestions,
  securityAnswers,
  setSecurityAnswers,
  currentPasswordForSecurity,
  setCurrentPasswordForSecurity,
  updatingEmail,
  updatingPhone,
  updatingPassword,
  updatingSecurityQuestions,
  updateEmail,
  updatePhone,
  handlePasswordChange,
  handleSecurityQuestionsUpdate
}) => {
  return (
    <Card data-testid="account-settings-card">
      <CardHeader>
        <CardTitle>Account Settings</CardTitle>
        <CardDescription>
          Manage your login credentials and security settings
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Email Change Section */}
        <div className="border-b pb-6">
          <Label className="text-base font-semibold">Email Address</Label>
          <p className="text-sm text-gray-500 mb-3">Current: {user?.email}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Enter new email address"
              className="flex-1 w-full"
            />
            <Button
              type="button"
              onClick={updateEmail}
              disabled={updatingEmail || !newEmail}
              variant="outline"
              className="w-full sm:w-auto whitespace-nowrap"
            >
              {updatingEmail ? 'Updating...' : 'Update Email'}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">You&apos;ll use this email to login</p>
        </div>

        {/* Phone Change Section */}
        <div className="border-b pb-6">
          <Label className="text-base font-semibold">Phone Number</Label>
          <p className="text-sm text-gray-500 mb-3">Current: {user?.phone || 'Not set'}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="Enter new phone number"
              className="flex-1 w-full"
            />
            <Button
              type="button"
              onClick={updatePhone}
              disabled={updatingPhone || !newPhone}
              variant="outline"
              className="w-full sm:w-auto whitespace-nowrap"
            >
              {updatingPhone ? 'Updating...' : 'Update Phone'}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-1">Your contact phone number</p>
        </div>

        {/* Password Change Section */}
        <div className="border-b pb-6">
          <Label className="text-base font-semibold">Change Password</Label>
          <p className="text-sm text-gray-500 mb-3">Update your account password</p>
          <form onSubmit={handlePasswordChange} className="space-y-3">
            <div>
              <Label htmlFor="currentPassword">Current Password *</Label>
              <Input
                id="currentPassword"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                placeholder="Enter current password"
              />
            </div>
            <div>
              <Label htmlFor="newPassword">New Password *</Label>
              <Input
                id="newPassword"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                placeholder="Enter new password"
              />
              <p className="text-xs text-gray-500 mt-1">Minimum 10 characters, 3+ character types</p>
            </div>
            <div>
              <Label htmlFor="confirmPassword">Confirm New Password *</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                placeholder="Confirm new password"
              />
            </div>
            <Button
              type="submit"
              disabled={updatingPassword}
              variant="outline"
              fullWidth="mobile"
            >
              {updatingPassword ? 'Changing...' : 'Change Password'}
            </Button>
          </form>
        </div>

        {/* Security Questions Section */}
        <div>
          <Label className="text-base font-semibold">Update Security Questions</Label>
          <p className="text-sm text-gray-500 mb-3">Used for password recovery</p>
          <form onSubmit={handleSecurityQuestionsUpdate} className="space-y-3">
            {securityQuestions.map((question, index) => (
              <div key={index}>
                <Label>{question} *</Label>
                <Input
                  type="text"
                  value={securityAnswers[index]?.answer || ''}
                  onChange={(e) => {
                    const newAnswers = [...securityAnswers];
                    newAnswers[index] = { question, answer: e.target.value };
                    setSecurityAnswers(newAnswers);
                  }}
                  placeholder="Your answer"
                />
              </div>
            ))}
            <div>
              <Label htmlFor="currentPasswordSecurity">Current Password (to confirm) *</Label>
              <Input
                id="currentPasswordSecurity"
                type="password"
                value={currentPasswordForSecurity}
                onChange={(e) => setCurrentPasswordForSecurity(e.target.value)}
                placeholder="Enter current password"
              />
            </div>
            <Button
              type="submit"
              disabled={updatingSecurityQuestions}
              variant="outline"
              fullWidth="mobile"
            >
              {updatingSecurityQuestions ? 'Updating...' : 'Update Questions'}
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default VendorAccountSettings;
